// Calendar-event commands (calendarEvents/{id}: an appointment — a time, usually a person or a place). Pure.
import { del, set, update } from './writes.js';
import { localDateStr } from '../time.js';

const now = (ctx) => (typeof ctx.now === 'function' ? ctx.now() : ctx.now ?? Date.now());
export const EVENT_KINDS = ['call', 'visit', 'meeting', 'install', 'personal', 'other'];
export const EVENT_STATUSES = ['planned', 'done', 'cancelled', 'no-show'];
const need = (ctx, id) => { const e = (ctx.events || []).find((x) => x.id === id); if (!e) throw new Error(`no event ${id}`); return e; };
const midnight = (date) => new Date(`${date}T00:00:00`).getTime();

/** start/end as ms, or `date` + `startMin`/`endMin` (minutes), or allDay on `date`. */
export function makeEventDoc(input, at, newId) {
  const title = String(input.title || '').trim();
  if (!title) throw new Error('title required');
  let { start = null, end = null } = input;
  const date = input.date || (start ? localDateStr(start) : null);
  if (!date) throw new Error('date (or start) required');
  if (start == null && Number.isFinite(input.startMin)) start = midnight(date) + input.startMin * 60000;
  if (end == null && Number.isFinite(input.endMin)) end = midnight(date) + input.endMin * 60000;
  const allDay = input.allDay === true || start == null;
  if (!allDay) {
    if (end == null) end = start + (Number(input.minutes) || 60) * 60000;
    if (!(end > start)) throw new Error('end must be after start');
  }
  const kind = EVENT_KINDS.includes(input.kind) ? input.kind : 'other';
  return {
    id: input.id || newId(), title, kind, date, start: allDay ? null : start, end: allDay ? null : end, allDay,
    customerId: input.customerId || null, customerName: input.customerName || null, phone: input.phone || null, address: input.address || null,
    projectId: input.projectId || null, departmentId: input.departmentId || null, notes: input.notes || '',
    remind: Array.isArray(input.remind) ? input.remind.filter((r) => r && Number.isFinite(Number(r.minutesBefore))).map((r) => ({ minutesBefore: Number(r.minutesBefore), style: r.style || 'banner' })) : [],
    ...(input.repeat ? { repeat: input.repeat } : {}),
    status: 'planned', outcome: null, source: input.source || null, tags: Array.isArray(input.tags) ? input.tags : [],
    createdAt: at, updatedAt: at,
  };
}

export function addEvent(ctx, input) {
  const project = input.projectId ? (ctx.projects || []).find((p) => p.id === input.projectId) : null;
  if (input.projectId && ctx.projects && !project) throw new Error(`no project ${input.projectId}`);
  const event = makeEventDoc({ ...input, departmentId: input.departmentId ?? project?.departmentId ?? null }, now(ctx), ctx.newId);
  return { writes: [set(`calendarEvents/${event.id}`, event)], event };
}

const E_EDITABLE = new Set(['title', 'kind', 'date', 'start', 'end', 'allDay', 'customerId', 'customerName', 'phone', 'address', 'projectId', 'departmentId', 'notes', 'remind', 'repeat', 'status', 'outcome', 'tags']);

export function updateEvent(ctx, { id, ...patch }) {
  const cur = need(ctx, id);
  const data = {};
  for (const [k, v] of Object.entries(patch)) if (E_EDITABLE.has(k) && v !== undefined) data[k] = v;
  if (!Object.keys(data).length) throw new Error('nothing to update');
  makeEventDoc({ ...cur, ...data }, 0, () => cur.id);   // validates the merged event
  data.updatedAt = now(ctx);
  return { writes: [update(`calendarEvents/${id}`, data)], patch: data };
}

/** Reschedule: new date and/or start (ms or startMin) keeping the length unless end is given. */
export function moveEvent(ctx, { id, date, start, startMin, end, endMin }) {
  const cur = need(ctx, id);
  const d = date || (start ? localDateStr(start) : cur.date);
  let s = start ?? (Number.isFinite(startMin) ? midnight(d) + startMin * 60000 : null);
  if (s == null && cur.start != null && d !== cur.date) s = midnight(d) + (cur.start - midnight(cur.date));
  if (s == null) s = cur.start;
  const len = cur.end != null && cur.start != null ? cur.end - cur.start : 60 * 60000;
  const e = end ?? (Number.isFinite(endMin) ? midnight(d) + endMin * 60000 : (s != null ? s + len : null));
  return updateEvent(ctx, { id, date: d, start: s, end: e, allDay: s == null });
}

export function setEventStatus(ctx, { id, status, outcome = null }) {
  if (!EVENT_STATUSES.includes(status)) throw new Error(`status must be one of ${EVENT_STATUSES.join(', ')}`);
  need(ctx, id);
  return updateEvent(ctx, { id, status, outcome: outcome || null });
}

export function deleteEvent(ctx, { id }) {
  need(ctx, id);
  return { writes: [del(`calendarEvents/${id}`)] };
}
